import { db } from '~/config/firebase.config';
import { getApp } from 'firebase/app';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
    collection,
    deleteField,
    doc,
    getDoc,
    getDocs,
    limit,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    setDoc,
    updateDoc,
    deleteDoc,
} from 'firebase/firestore';

const getQuizzesRef = (clientID: string, gameID: string) =>
    collection(db, 'clients', clientID, 'games', gameID, 'quizzes');

/**
 * Remove undefined values from an object (Firestore doesn't accept undefined)
 */
function removeUndefined(obj: any): any {
    if (Array.isArray(obj)) {
        return obj.map(removeUndefined);
    }
    if (obj !== null && typeof obj === 'object') {
        return Object.entries(obj).reduce((acc, [key, value]) => {
            if (value !== undefined) {
                acc[key] = removeUndefined(value);
            }
            return acc;
        }, {} as any);
    }
    return obj;
}

/**
 * Les éditeurs envoient `null` pour « champ retiré » (cover/catégorie/description) :
 * en update, null → deleteField() (un champ absent du payload serait simplement
 * ignoré par updateDoc et l'ancienne valeur survivrait) ; en création, on le retire.
 */
function mapNullsForUpdate(obj: any): any {
    return Object.entries(obj).reduce((acc, [key, value]) => {
        acc[key] = value === null ? deleteField() : value;
        return acc;
    }, {} as any);
}

function removeNulls(obj: any): any {
    return Object.entries(obj).reduce((acc, [key, value]) => {
        if (value !== null) acc[key] = value;
        return acc;
    }, {} as any);
}

/**
 * Save or update a quiz to Firebase
 * This is called by the console (authenticated) when the game sends a postMessage
 */
async function saveQuiz(clientID: string, gameID: string, quizId: string, quizData: any): Promise<void> {
    const quizRef = doc(db, 'clients', clientID, 'games', gameID, 'quizzes', quizId);

    try {
        const document = await getDoc(quizRef);
        // Remove undefined values (Firestore doesn't accept them)
        const cleanedData = removeUndefined(quizData);

        if (document.exists()) {
            // Update existing quiz — null au premier niveau = suppression du champ
            await updateDoc(quizRef, {
                ...mapNullsForUpdate(cleanedData),
                updatedAt: serverTimestamp(),
            });
        } else {
            // Create new quiz — pas de deleteField possible dans un setDoc
            await setDoc(quizRef, {
                ...removeNulls(cleanedData),
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
        }
    } catch (err) {
        console.error('Cannot save quiz:', err);
        throw err;
    }
}

/**
 * Retrieve a specific quiz from Firebase
 */
async function retrieveQuiz(clientID: string, gameID: string, quizId: string): Promise<any> {
    const quizRef = doc(db, 'clients', clientID, 'games', gameID, 'quizzes', quizId);

    try {
        const document = await getDoc(quizRef);
        if (document.exists()) {
            const data = document.data();
            return {
                ...data,
                id: document.id,
                createdAt:
                    data.createdAt && typeof data.createdAt.toDate === 'function'
                        ? data.createdAt.toDate()
                        : new Date(),
                updatedAt:
                    data.updatedAt && typeof data.updatedAt.toDate === 'function'
                        ? data.updatedAt.toDate()
                        : new Date(),
            };
        }
        return null;
    } catch (err) {
        console.error('Cannot retrieve quiz:', err);
        throw err;
    }
}

/**
 * Retrieve all quizzes from Firebase
 */
async function retrieveQuizzes(clientID: string, gameID: string, total: number): Promise<any[]> {
    const limitValue = total <= 0 ? 10000 : total;
    const quizzesRef = getQuizzesRef(clientID, gameID);
    const q = query(quizzesRef, orderBy('updatedAt', 'desc'), limit(limitValue));

    try {
        const querySnapshot = await getDocs(q);
        const quizzes: any[] = [];

        querySnapshot.forEach((document) => {
            const data = document.data();
            quizzes.push({
                ...data,
                id: document.id,
                createdAt:
                    data.createdAt && typeof data.createdAt.toDate === 'function'
                        ? data.createdAt.toDate()
                        : new Date(),
                updatedAt:
                    data.updatedAt && typeof data.updatedAt.toDate === 'function'
                        ? data.updatedAt.toDate()
                        : new Date(),
            });
        });

        return quizzes;
    } catch (err) {
        console.error('Cannot retrieve quizzes:', err);
        throw err;
    }
}

/**
 * Delete a quiz from Firebase
 */
async function deleteQuiz(clientID: string, gameID: string, quizId: string): Promise<void> {
    const quizRef = doc(db, 'clients', clientID, 'games', gameID, 'quizzes', quizId);

    try {
        await deleteDoc(quizRef);
    } catch (err) {
        console.error('Cannot delete quiz:', err);
        throw err;
    }
}

/**
 * Subscribe to quizzes changes in real-time
 * @param clientID the client identifier
 * @param gameID the game identifier
 * @returns a subscription function
 */
function subscribeToQuizzes(clientID: string, gameID: string) {
    const quizzesRef = getQuizzesRef(clientID, gameID);
    return (onValueChange: () => void) => onSnapshot(quizzesRef, onValueChange);
}

export const QuizzesAPI = {
    saveQuiz,
    retrieveQuiz,
    retrieveQuizzes,
    deleteQuiz,
    subscribeToQuizzes,
    ref: getQuizzesRef,
    async uploadCoverImageFromDataUrl(params: {
        clientID: string;
        gameID: string;
        quizId: string;
        fileName: string;
        dataUrl: string;
    }) {
        const { clientID, gameID, quizId, fileName, dataUrl } = params;
        // Ensure there's an authenticated user (anonymous if necessary)
        const auth = getAuth();
        if (!auth.currentUser) {
            try {
                await signInAnonymously(auth);
            } catch (e) {
                /* ignore */
            }
        }
        const storage = getStorage(getApp());
        const safeName = fileName.replace(/\s+/g, '_');
        const path = `${clientID}/quizzes/${quizId}/cover-image/${safeName}`;
        const objectRef = ref(storage, path);

        // Parse data URL
        const match = dataUrl.match(/^data:(.*?);base64,(.*)$/);
        if (!match) throw new Error('Invalid data URL');
        const contentType = match[1] || 'application/octet-stream';
        const base64 = match[2];
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);

        await uploadBytes(objectRef, byteArray, { contentType });
        const url = await getDownloadURL(objectRef);

        const quizRef = doc(db, 'clients', clientID, 'games', gameID, 'quizzes', quizId);
        await updateDoc(quizRef, { coverImage: url, coverImageUrl: url, coverImagePath: path });

        return { url, path };
    },

    /**
     * Upload générique d'une image de quiz (couverture OU image de question).
     * Contrairement à uploadCoverImageFromDataUrl, N'ÉCRIT RIEN dans Firestore :
     * l'URL renvoyée est persistée par saveQuiz (coverImage / questions[].imageUrl).
     * Fonctionne donc AVANT la 1re sauvegarde (aucun doc requis), et `folder`
     * isole les images par question (ex. `questions/<qid>`).
     */
    async uploadImageFromDataUrl(params: {
        clientID: string;
        gameID: string;
        quizId: string;
        folder: string;
        fileName: string;
        dataUrl: string;
    }) {
        const { clientID, quizId, folder, fileName, dataUrl } = params;
        const auth = getAuth();
        if (!auth.currentUser) {
            try {
                await signInAnonymously(auth);
            } catch (e) {
                /* ignore */
            }
        }
        const storage = getStorage(getApp());
        // Préfixe horodaté : ré-uploader un fichier du même nom (« image.jpg »)
        // écraserait l'objet et ferait tourner son token de download → l'URL
        // déjà persistée dans Firestore casserait (403).
        const safeName = `${Date.now()}-${fileName.replace(/\s+/g, '_')}`;
        const safeFolder = folder.replace(/[^a-zA-Z0-9/_-]/g, '_') || 'images';
        const path = `${clientID}/quizzes/${quizId}/${safeFolder}/${safeName}`;
        const objectRef = ref(storage, path);

        const match = dataUrl.match(/^data:(.*?);base64,(.*)$/);
        if (!match) throw new Error('Invalid data URL');
        const contentType = match[1] || 'application/octet-stream';
        // fetch(dataUrl) décode en natif — pas de boucle atob char-par-char qui
        // gèle le main thread (qui héberge l'iframe du jeu) sur une grosse image.
        const blob = await (await fetch(dataUrl)).blob();

        await uploadBytes(objectRef, blob, { contentType });
        const url = await getDownloadURL(objectRef);

        return { url, path };
    },
};
